import { Accessor } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import {
  FeatureToggle,
  FeatureToggleCapabilityCoordinator,
  PaneVisibilityCoordinator,
  ViewMode,
} from '@mobile-mapping/web3d';
import type {
  RangeRowConfig,
  SliderRowConfig,
  ToggleRowConfig,
  VisibilityRowConfig,
} from './VisibilityControl';

const MAX_OPACITY_PERCENT = 100;
const MODEL_MIN_OPACITY_PERCENT = 20;
// Image panes extend the models slider below its opacity floor with a single
// hide stop, so 1-19 stays unreachable and 0 means "hide the models".
const MODEL_HIDE_PERCENT = 0;
const POINT_CLOUD_MIN_OPACITY_PERCENT = 0;
const STATION_MIN_OPACITY_PERCENT = 0;
const IMAGE_MIN_OPACITY_PERCENT = 0;
// 0m is the "off" position: ImagePointcloudCapability auto-deactivates at
// distance == 0 and the coordinator hides the loaded cloud (root.visible)
// without unloading it.
const CLIP_DISTANCE_MIN_M = 0;
const CLIP_DISTANCE_MAX_M = 500;
const CLIP_DISTANCE_STEP_M = 1;

const percentFromLevel = (level: number | null): number =>
  level ?? MAX_OPACITY_PERCENT;

const levelFromPercent = (percent: number): number | null =>
  percent >= MAX_OPACITY_PERCENT ? null : percent;

/** Only 0 hides the models; any value above it shows them at the opacity floor or more. */
const snapModelOpacityPercent = (next: number): number =>
  next === MODEL_HIDE_PERCENT
    ? MODEL_HIDE_PERCENT
    : Math.max(next, MODEL_MIN_OPACITY_PERCENT);

const featureToggleByKey = (
  coord: FeatureToggleCapabilityCoordinator,
  key: string
): FeatureToggle | undefined =>
  coord.features().find((ft) => ft.feature.key === key);

const toggleRowFromFeature = (ft: FeatureToggle): ToggleRowConfig => ({
  kind: 'toggle',
  id: ft.feature.key,
  labelKey: `mobileMapping.featureToggle.${ft.feature.key}`,
  value: ft.visible,
  onChange: (next: boolean) => {
    if (ft.visible() !== next) ft.toggle();
  },
});

const collectToggleRows = (
  coord: FeatureToggleCapabilityCoordinator,
  desiredKeys: readonly string[]
): ToggleRowConfig[] =>
  desiredKeys
    .map((key) => featureToggleByKey(coord, key))
    .filter((ft): ft is FeatureToggle => ft !== undefined)
    .map(toggleRowFromFeature);

interface SliderRowInputs {
  readonly id: string;
  readonly labelKey: string;
  readonly sliderTestId: string;
  readonly min: number;
  readonly state: PaneVisibilityCoordinator['models'];
  readonly isDisabled?: Accessor<boolean>;
  readonly disabledTooltipKey?: string;
  snapPercent?(next: number): number;
}

const sliderRowFromState = (inputs: SliderRowInputs): SliderRowConfig => ({
  kind: 'slider',
  id: inputs.id,
  labelKey: inputs.labelKey,
  sliderTestId: inputs.sliderTestId,
  min: inputs.min,
  percent: () => percentFromLevel(inputs.state.effective()),
  // isDisabled and disabledTooltipKey will be addressed in NUMARK-1191
  isDisabled: inputs.isDisabled,
  disabledTooltipKey: inputs.disabledTooltipKey,
  link: {
    isLinked: inputs.state.linked,
    onRelink: () => inputs.state.relink(),
  },
  onChange: (percent) =>
    inputs.state.setLocal(
      levelFromPercent(inputs.snapPercent?.(percent) ?? percent)
    ),
});

const imageModeModelsRow = (
  state: PaneVisibilityCoordinator['models']
): SliderRowConfig =>
  sliderRowFromState({
    id: 'models',
    labelKey: 'mobileMapping.paneVisibility.modelOpacity',
    sliderTestId: 'pane-visibility-slider-models',
    min: MODEL_HIDE_PERCENT,
    state,
    snapPercent: snapModelOpacityPercent,
  });

const clipDistanceRangeRow = (
  state: PaneVisibilityCoordinator['clipDistance']
): RangeRowConfig => ({
  kind: 'range',
  id: 'clip-distance',
  labelKey: 'mobileMapping.paneVisibility.clipDistance',
  sliderTestId: 'pane-visibility-range-clip-distance',
  min: CLIP_DISTANCE_MIN_M,
  max: CLIP_DISTANCE_MAX_M,
  step: CLIP_DISTANCE_STEP_M,
  value: state.effective,
  formatValue: (v) => `${Math.round(v)} m`,
  link: {
    isLinked: state.linked,
    onRelink: () => state.relink(),
  },
  onChange: (value) => state.setLocal(value),
});

/**
 * Pure-functional row builder. Callers pass the current view-mode
 * discriminant and receive the row set to hand to
 * {@link VisibilityControl}; wrap in a memo at the call site if a
 * reactive accessor is needed.
 */
export const buildRowsForViewMode = (
  type: ViewMode['type'],
  paneCap: PaneVisibilityCoordinator,
  featureToggleCoord: FeatureToggleCapabilityCoordinator
): VisibilityRowConfig[] => {
  switch (type) {
    case '3D': {
      const models = sliderRowFromState({
        id: 'models',
        labelKey: 'mobileMapping.paneVisibility.modelOpacity',
        sliderTestId: 'pane-visibility-slider-models',
        min: MODEL_MIN_OPACITY_PERCENT,
        state: paneCap.models,
      });
      const pointClouds = sliderRowFromState({
        id: 'point-clouds',
        labelKey: 'mobileMapping.paneVisibility.pointCloudsOpacity',
        sliderTestId: 'pane-visibility-slider-point-clouds',
        min: POINT_CLOUD_MIN_OPACITY_PERCENT,
        state: paneCap.pointClouds,
      });
      const toggles = collectToggleRows(featureToggleCoord, [
        'frustum',
        'stationMarkers',
        'navigationPath',
      ]);
      return [
        pointClouds,
        models,
        ...(toggles.length > 0
          ? [{ kind: 'divider' as const, id: 'sliders-vs-toggles' }, ...toggles]
          : []),
      ];
    }

    case 'Panorama': {
      // PC visibility is driven by clip distance — set the range row to
      // zero to hide the cloud — so there is no separate PC on/off toggle.
      const pcOpacity = sliderRowFromState({
        id: 'point-clouds-opacity',
        labelKey: 'mobileMapping.paneVisibility.pointCloudsOpacity',
        sliderTestId: 'pane-visibility-slider-point-clouds-opacity',
        min: POINT_CLOUD_MIN_OPACITY_PERCENT,
        state: paneCap.pointClouds,
      });

      const clipDistance = clipDistanceRangeRow(paneCap.clipDistance);

      const models = imageModeModelsRow(paneCap.models);

      // Panoramas live at stations, so the stations slider applies here
      // and is linked to the global stations transparency.
      const stations = sliderRowFromState({
        id: 'stations',
        labelKey: 'mobileMapping.transparency.stations',
        sliderTestId: 'pane-visibility-slider-stations',
        min: STATION_MIN_OPACITY_PERCENT,
        state: paneCap.stations,
      });

      // Drag the stations slider to 0 to hide the panorama image; there
      // is no separate image toggle. Keeps panorama and planar menus
      // structurally aligned (opacity sliders own visibility, toggles
      // are reserved for overlays like markers / navigation path).
      const remainingToggles = collectToggleRows(featureToggleCoord, [
        'stationMarkers',
        'navigationPath',
      ]);

      const rows: VisibilityRowConfig[] = [
        pcOpacity,
        clipDistance,
        models,
        stations,
      ];
      if (remainingToggles.length > 0) {
        rows.push(
          { kind: 'divider', id: 'sliders-vs-toggles' },
          ...remainingToggles
        );
      }
      return rows;
    }

    case 'Image': {
      // Planar panes have no panorama / station mesh, so the stations
      // slider doesn't apply here — replace it with an image opacity
      // slider that lets users fade the planar photo to see point cloud
      // and models behind it. Visibility is owned by the opacity sliders
      // (image) and clip distance (PC), so neither has a separate toggle.
      const pcOpacity = sliderRowFromState({
        id: 'point-clouds-opacity',
        labelKey: 'mobileMapping.paneVisibility.pointCloudsOpacity',
        sliderTestId: 'pane-visibility-slider-point-clouds-opacity',
        min: POINT_CLOUD_MIN_OPACITY_PERCENT,
        state: paneCap.pointClouds,
      });

      const clipDistance = clipDistanceRangeRow(paneCap.clipDistance);

      const models = imageModeModelsRow(paneCap.models);

      // Local-only image opacity row. Suppress the link affordance —
      // there is no global "image" transparency upstream to sync with.
      const imageOpacity: SliderRowConfig = {
        ...sliderRowFromState({
          id: 'image-opacity',
          labelKey: 'mobileMapping.paneVisibility.imageOpacity',
          sliderTestId: 'pane-visibility-slider-image-opacity',
          min: IMAGE_MIN_OPACITY_PERCENT,
          state: paneCap.image,
        }),
        link: undefined,
      };

      const remainingToggles = collectToggleRows(featureToggleCoord, [
        'stationMarkers',
        'navigationPath',
      ]);

      const rows: VisibilityRowConfig[] = [
        pcOpacity,
        clipDistance,
        models,
        imageOpacity,
      ];
      if (remainingToggles.length > 0) {
        rows.push(
          { kind: 'divider', id: 'sliders-vs-toggles' },
          ...remainingToggles
        );
      }
      return rows;
    }

    case '2D': {
      return collectToggleRows(featureToggleCoord, [
        'frustum',
        'stationMarkers',
        'navigationPath',
      ]);
    }

    default:
      throw new UnreachableCaseError(type);
  }
};
